import { useParams } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import { LoginData } from '../components/LoginModal/LoginModal.types';
import useAxios from '../hooks/useAxios';
import { useLogin } from '../hooks/useLogin';
import {
  ResetPasswordData,
  UpdateInfoData,
  UpdatePasswordData
} from '../views/Profile/ProfileView.types';

import {
  EApiRoutes,
  getApiRouteOrMock,
  getCustomHeader
} from './api.utils';

interface UseUsersOptions {
  publisherId?: string;
}

export default function useUsers({
  publisherId
}: UseUsersOptions) {
  const axios = useAxios();
  const loginUtils = useLogin();
  const { portalId } = useParams();

  const updateUser = useMutation(async (data: Partial<UpdateInfoData>) => {
    return await axios.put(getApiRouteOrMock(EApiRoutes.USERS), data);
  }, {});

  const updateRole = useMutation(async (data: Partial<UpdateInfoData>) => {
    if (!data.userId || !data.role) {
      throw new Error(
        'UserID and role are required for updating the user role.'
      );
    }
    return await axios.put(
      getApiRouteOrMock(EApiRoutes.UPDATE_USER_ROLE) + `/${data.userId}`,
      { role: data.role }
    );
  }, {});

  const updateRoleV2 = useMutation(
    async (data: { userId: string; roleId: string }) => {
      if (!data.userId || !data.roleId) {
        throw new Error(
          'UserID and role are required for updating the user role.'
        );
      }
      return await axios.put(
        getApiRouteOrMock(EApiRoutes.UPDATE_USER_ROLE) + `/${data.userId}/role`,
        { roleId: data.roleId },
        portalId ? { 'x-portal': portalId } : getCustomHeader(publisherId)
      );
    },
    {}
  );

  const deleteUser = useMutation(async (id: string) => {
    return await axios.del(
      `${getApiRouteOrMock(EApiRoutes.DELETE_USER)}/${id}`,
      {},
      portalId ? { 'x-portal': portalId } : getCustomHeader(publisherId)
    );
  }, {});

  const addUser = useMutation(async (userForm: any) => {
    return await axios.post(
      getApiRouteOrMock(EApiRoutes.ADD_USER),
      userForm,
      publisherId ? { 'x-publishers': publisherId } : undefined
    );
  }, {});

  const addUserToPortal = useMutation(async (userForm: any) => {
    return await axios.post(
      getApiRouteOrMock(EApiRoutes.ADD_USER_TO_PORTAL),
      userForm,
      portalId ? { 'x-portal': portalId } : undefined
    );
  }, {});

  const resendUserInvitation = useMutation(async (userData: any) => {
    return await axios.post(
      getApiRouteOrMock(EApiRoutes.RESEND_USER_INVITATION),
      userData,
      portalId ? { 'x-portal': portalId } : getCustomHeader(publisherId)
    );
  }, {});

  const resetPassword = useMutation(async (data: ResetPasswordData) => {
    return await axios.put(getApiRouteOrMock(EApiRoutes.RESET_USER), data);
  }, {});

  const updatePassword = useMutation(async (data: UpdatePasswordData) => {
    return await axios.put(
      getApiRouteOrMock(EApiRoutes.UPDATE_USER_PASSWORD),
      data
    );
  }, {});

  const forgotPassword = useMutation(async (email: string) => {
    return await axios.put(getApiRouteOrMock(EApiRoutes.FORGOT_USER_PASSWORD), {
      email
    });
  }, {});

  const login = useMutation(async (loginData: LoginData) => {
    const response = await axios.post(
      getApiRouteOrMock(EApiRoutes.LOGIN_USER),
      loginData
    );
    return response.data;
  }, {});

  const loginWithGoogle = useMutation(async (loginData: LoginData) => {
    const response = await axios.post(
      getApiRouteOrMock(EApiRoutes.LOGIN_USER_GOOGLE),
      loginData
    );
    return response.data;
  }, {});

  const loginWithMicrosoft = useMutation(async (loginData: LoginData) => {
    const response = await axios.post(
      getApiRouteOrMock(EApiRoutes.LOGIN_USER_MICROSOFT),
      loginData
    );
    return response.data;
  }, {});

  const loginWithToken = useMutation(async (loginData: LoginData) => {
    const response = await axios.post(
      getApiRouteOrMock(EApiRoutes.LOGIN_USER_TOKEN),
      loginData
    );
    return response.data;
  }, {});

  const logout = useMutation(async () => {
    await axios.post(getApiRouteOrMock(EApiRoutes.LOGOUT_USER), {});
    loginUtils.cleanUserLoginData();
  }, {});

  return {
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithToken,
    logout,
    addUser,
    addUserToPortal,
    updateUser,
    updateRole,
    updateRoleV2,
    deleteUser,
    resetPassword,
    updatePassword,
    forgotPassword,
    resendUserInvitation
  };
}
